<script setup lang="ts">
import { useEventListener } from '@vueuse/core';
import { computed, ref } from 'vue';

import type { FiscalShiftAction } from '@/entities/fiscalization';

import { useAuthStore } from '@/entities/auth';
import { formatMoney, parseRublesToKopecks, useConfirm } from '@/shared/lib';
import { AppButton, AppInput, AppTextarea, BaseModal } from '@/shared/ui';

import type { CashShiftUiAction } from '../model/cash-shift-ui-events';
import type { UseFiscalShiftStatusReturn } from '../model/use-fiscal-shift-status';

import { CASH_SHIFT_UI_EVENT } from '../model/cash-shift-ui-events';

/**
 * Модалки открытия, восстановления и закрытия смены. На странице ровно один экземпляр (в плашке
 * локации): он же единственный слушатель `CASH_SHIFT_UI_EVENT` от блокеров оплаты.
 */
const props = defineProps<{ status: UseFiscalShiftStatusReturn }>();
/** Смена закрыта обычным путём с Z-отчётом; аварийное закрытие не в счёт. */
const emit = defineEmits<{ closed: [] }>();
const authStore = useAuthStore();
const { confirm } = useConfirm();
const modalMode = ref<'closing' | 'opening' | 'opening_recovery' | null>(null);
const openingCashInput = ref('');
const closingCashInput = ref('');
const closingComment = ref('');
const emergencyReason = ref('');
const {
  actionError,
  cancelClosing,
  cancelOpening,
  closeRefused,
  closingPreparation,
  completeClosing,
  continueOpening,
  currentShift,
  cycleProgress,
  forceClose,
  isActionPending,
  openShift,
  operationalStatus,
  pendingAction,
  prepareClosing,
  printXReport,
  reopenShift,
} = props.status;
const canForceClose = computed(() => authStore.hasPermission('shifts.force_close'));
const isFiscalizationEnabled = computed(() => operationalStatus.value?.fiscalization_enabled === true);
const revenueMismatch = computed(() => {
  const preparation = closingPreparation.value;

  return preparation?.kind === 'ready' && preparation.revenueKktMinor !== null
    ? preparation.revenueKktMinor - preparation.context.revenue_mors_cash - preparation.context.revenue_mors_card
    : null;
});
/**
 * Сколько наличных должно быть в ящике: остаток на открытие плюс наличная выручка минус
 * наличные возвраты (`revenue_mors_cash` уже нетто). Складываем на экране закрытия, а не
 * заставляем кассира считать это в уме.
 */
const expectedClosingCash = computed(() => {
  const preparation = closingPreparation.value;
  const shift = currentShift.value;

  return preparation?.kind === 'ready' && shift != null
    ? Math.round(shift.opening_cash * 100) + preparation.context.revenue_mors_cash
    : null;
});
/** Отрицательное — недостача. `null`, пока кассир не ввёл сумму. */
const closingCashMismatch = computed(() => {
  const expected = expectedClosingCash.value;
  const counted = parseRublesToKopecks(closingCashInput.value);

  return expected === null || counted === null ? null : counted - expected;
});

const beginAction = async (action: Exclude<FiscalShiftAction, null>): Promise<void> => {
  if (action === 'open') {
    openingCashInput.value = '';
    modalMode.value = 'opening';
    return;
  }

  if (action === 'continue_opening') {
    modalMode.value = 'opening_recovery';
    return;
  }

  if (action === 'close' || action === 'continue_closing') {
    await showClosing();
    return;
  }

  const accepted = await confirm({
    cancelText: 'Отмена',
    confirmText: 'Продолжить работу',
    message: 'Смена в Морсе продолжается. На кассе будет открыта новая смена.',
    title: 'Смена на кассе была закрыта извне',
  });

  if (accepted) await reopenShift();
};

const submitOpening = async (): Promise<void> => {
  const openingCash = parseRubles(openingCashInput.value);

  if (openingCash === null) return;

  const outcome = await openShift(openingCash, async () =>
    confirm({
      cancelText: 'Отмена',
      confirmText: 'Закрыть смену на кассе и открыть новую',
      message: 'На кассе уже открыта смена. Для начала работы её нужно закрыть — будет напечатан Z-отчёт.',
      title: 'На кассе уже открыта смена',
    }),
  );

  if (outcome === 'opened') {
    modalMode.value = null;
    return;
  }

  // Смена уже создана и висит в `opening`: поле наличных здесь бесполезно, а повторный
  // сабмит упрётся в уникальный индекс на локацию. Показываем «Продолжить» / «Отменить».
  if (outcome === 'recovery_required') modalMode.value = 'opening_recovery';
};

const resumeOpening = async (): Promise<void> => {
  if (await continueOpening()) modalMode.value = null;
};

const abortOpening = async (): Promise<void> => {
  if (await cancelOpening()) modalMode.value = null;
};

const showClosing = async (): Promise<void> => {
  closingCashInput.value = '';
  closingComment.value = '';
  emergencyReason.value = '';
  modalMode.value = 'closing';
  await prepareClosing();
};

const finishClosing = async (): Promise<void> => {
  const closingCash = parseRubles(closingCashInput.value);

  if (closingCash === null) return;

  if (await completeClosing({ closingCash, comment: closingComment.value })) {
    modalMode.value = null;
    emit('closed');
  }
};

const abortClosing = async (): Promise<void> => {
  if (await cancelClosing()) {
    modalMode.value = null;
  }
};

const emergencyClose = async (): Promise<void> => {
  const closingCash = parseRubles(closingCashInput.value);

  if (closingCash === null || emergencyReason.value.trim() === '') return;

  const accepted = await confirm({
    cancelText: 'Отмена',
    confirmText: 'Аварийно закрыть',
    message: 'Смена в Морсе закроется без Z-отчёта. Смену на кассе закройте, когда она заработает.',
    title: 'Закрыть смену аварийно?',
  });

  if (accepted && (await forceClose(closingCash, emergencyReason.value))) modalMode.value = null;
};

const handleRequestedAction = (event: Event): void => {
  const action = (event as CustomEvent<CashShiftUiAction>).detail;
  void beginAction(action);
};

useEventListener(typeof window === 'undefined' ? undefined : window, CASH_SHIFT_UI_EVENT, handleRequestedAction);

defineExpose({ beginAction });

/** Смены хранят наличные в рублях, а общий парсер отдаёт копейки. */
function parseRubles(value: string): number | null {
  const kopecks = parseRublesToKopecks(value);

  return kopecks === null ? null : kopecks / 100;
}

function formatMajorMoney(value: number): string {
  return formatMoney(Math.round(value * 100)) ?? '—';
}
</script>

<template>
  <BaseModal
    :show="modalMode === 'opening'"
    size="sm"
    @update:show="modalMode = $event ? 'opening' : null"
  >
    <template #title>Открыть смену</template>
    <p class="mb-4 text-sm text-(--base-secondary-text-color)">Пересчитайте деньги в ящике до первой оплаты.</p>
    <AppInput
      v-model="openingCashInput"
      label="Наличных в кассе, ₽"
      inputmode="decimal"
      autofocus
      required
    />
    <template #actions>
      <div class="flex justify-end gap-2">
        <AppButton
          outline
          @click="modalMode = null"
          >Отмена</AppButton
        >
        <AppButton
          :loading="isActionPending"
          :disabled="parseRubles(openingCashInput) === null"
          @click="submitOpening"
        >
          Открыть смену
        </AppButton>
      </div>
    </template>
  </BaseModal>

  <BaseModal
    :show="modalMode === 'opening_recovery'"
    size="sm"
    @update:show="modalMode = $event ? 'opening_recovery' : null"
  >
    <template #title>Открытие смены не завершено</template>
    <p class="text-sm leading-6 text-(--base-secondary-text-color)">
      Продолжите операцию на этом рабочем месте или отмените незавершённое открытие.
    </p>
    <p
      v-if="actionError !== null"
      class="mt-3 rounded-xl border border-(--base-warning-color)/40 bg-(--base-warning-color)/10 p-4 text-sm leading-6"
    >
      {{ actionError }}
    </p>
    <template #actions>
      <div class="flex flex-wrap justify-end gap-2">
        <AppButton
          outline
          :loading="pendingAction === 'cancel_opening'"
          @click="abortOpening"
          >Отменить</AppButton
        >
        <AppButton
          :loading="pendingAction === 'continue_opening'"
          @click="resumeOpening"
          >Продолжить</AppButton
        >
      </div>
    </template>
  </BaseModal>

  <BaseModal
    :show="modalMode === 'closing'"
    size="md"
    @update:show="modalMode = $event ? 'closing' : null"
  >
    <template #title>Закрыть смену</template>

    <div
      v-if="closingPreparation?.kind === 'kkt_unavailable'"
      class="space-y-4"
    >
      <div class="rounded-xl border border-(--base-error-color)/40 bg-(--base-error-color)/10 p-4">
        <p class="font-semibold">Касса недоступна</p>
        <!-- `message` — инструкция хендшейка или ошибка GetCounters: она уже называет слой,
             который отвалился (расширение, программа KKMServer, ККТ). -->
        <p class="mt-1 text-sm">
          {{ closingPreparation.message }}
          <template v-if="canForceClose">
            Если это не помогло, закройте смену аварийно: Z-отчёт не будет получен.
          </template>
          <template v-else> Если это не помогло, смену закроет сотрудник с правом аварийного закрытия. </template>
        </p>
      </div>
      <template v-if="canForceClose">
        <AppInput
          v-model="closingCashInput"
          label="Наличных по факту, ₽"
          inputmode="decimal"
          required
        />
        <AppTextarea
          v-model="emergencyReason"
          placeholder="Причина аварийного закрытия"
        />
      </template>
    </div>

    <div
      v-else-if="closingPreparation?.kind === 'ready'"
      class="space-y-5"
    >
      <div class="grid gap-3 sm:grid-cols-2">
        <div class="rounded-xl bg-(--base-element-color) p-3">
          <p class="text-xs text-(--base-secondary-text-color)">Выручка Морса</p>
          <p class="mt-1 text-xl font-semibold tabular-nums">
            {{ formatMoney(closingPreparation.context.revenue_mors_total) }}
          </p>
          <p class="mt-1 text-xs text-(--base-secondary-text-color)">
            Нал {{ formatMoney(closingPreparation.context.revenue_mors_cash) }} · карта
            {{ formatMoney(closingPreparation.context.revenue_mors_card) }}
          </p>
        </div>
        <div
          v-if="isFiscalizationEnabled"
          class="rounded-xl p-3"
          :class="revenueMismatch === 0 ? 'bg-(--base-success-color)/10' : 'bg-(--base-warning-color)/10'"
        >
          <p class="text-xs text-(--base-secondary-text-color)">Выручка ККТ</p>
          <p class="mt-1 text-xl font-semibold tabular-nums">
            {{
              closingPreparation.revenueKktMinor === null
                ? 'Нет данных'
                : formatMoney(closingPreparation.revenueKktMinor)
            }}
          </p>
          <p
            v-if="revenueMismatch !== null"
            class="mt-1 text-xs"
          >
            Расхождение: {{ formatMoney(revenueMismatch) }}
          </p>
        </div>
      </div>
      <p class="text-sm text-(--base-secondary-text-color)">
        На начало смены: {{ formatMajorMoney(currentShift?.opening_cash ?? 0) }}
      </p>
      <div
        class="rounded-xl p-3"
        :class="
          closingCashMismatch === null || closingCashMismatch === 0
            ? 'bg-(--base-success-color)/10'
            : 'bg-(--base-warning-color)/10'
        "
      >
        <p class="text-xs text-(--base-secondary-text-color)">Ожидается в кассе</p>
        <p class="mt-1 text-xl font-semibold tabular-nums">
          {{ expectedClosingCash === null ? 'Нет данных' : formatMoney(expectedClosingCash) }}
        </p>
        <p
          v-if="closingCashMismatch !== null"
          class="mt-1 text-xs"
        >
          Расхождение: {{ formatMoney(closingCashMismatch) }}
        </p>
      </div>
      <AppInput
        v-model="closingCashInput"
        label="Наличных по факту, ₽"
        inputmode="decimal"
        required
      />
      <AppTextarea
        v-model="closingComment"
        placeholder="Комментарий к смене (необязательно)"
      />
      <p
        v-if="cycleProgress !== null"
        class="text-sm text-(--base-secondary-text-color)"
        aria-live="polite"
      >
        {{ cycleProgress }}
      </p>
      <div
        v-if="closeRefused && canForceClose"
        class="space-y-3 rounded-xl border border-(--base-error-color)/40 bg-(--base-error-color)/10 p-4"
      >
        <p class="text-sm">Касса не закрыла смену. Повторите закрытие или закройте смену аварийно.</p>
        <AppTextarea
          v-model="emergencyReason"
          placeholder="Причина аварийного закрытия"
        />
      </div>
      <AppButton
        v-if="closingPreparation.deviceId"
        outline
        :loading="pendingAction === 'x_report'"
        @click="printXReport"
      >
        Печать X-отчёта
      </AppButton>
    </div>

    <div
      v-else
      class="flex min-h-32 items-center justify-center text-sm text-(--base-secondary-text-color)"
    >
      Готовим закрытие…
    </div>

    <template #actions>
      <div class="flex flex-wrap justify-end gap-2">
        <AppButton
          outline
          :loading="pendingAction === 'cancel_closing'"
          @click="abortClosing"
          >Отмена</AppButton
        >
        <AppButton
          v-if="closingPreparation?.kind === 'ready'"
          :loading="pendingAction === 'close'"
          :disabled="parseRubles(closingCashInput) === null"
          @click="finishClosing"
        >
          Закрыть смену
        </AppButton>
        <AppButton
          v-if="(closingPreparation?.kind === 'kkt_unavailable' || closeRefused) && canForceClose"
          :loading="pendingAction === 'force_close'"
          :disabled="parseRubles(closingCashInput) === null || emergencyReason.trim() === ''"
          @click="emergencyClose"
        >
          Аварийно закрыть
        </AppButton>
      </div>
    </template>
  </BaseModal>
</template>
